import * as React from 'react'
import { FunctionComponent, useState } from 'react'
import { EntityDto } from '../../config/appConfig'
import BaseOverview, { BaseOverviewCount, BaseOverviewProps } from '../BaseOverview/BaseOverview'
import { BaseOverviewFilterType } from '../BaseOverview/BaseOverviewFilter'
import { BaseOverviewSortingType } from '../BaseOverview/BaseSortMenu'
import HammockFormDialog, { HammockFormDialogProps } from '../../v2/components/Dialog/HammockFormDialog'
import { FormikConfig } from 'formik/dist/types'

/**
 * Basis for overview page props. As little as possible is required.
 */
export type BaseMutableOverviewProps<
    EntityT extends EntityDto,
    FilterT extends BaseOverviewFilterType,
    SortingT extends BaseOverviewSortingType<any>,
    HeaderT extends BaseOverviewCount = BaseOverviewCount,
    ActionButtonPropsT = any,
    DialogBodyEntityT = object,
    DialogBodyPropsT extends BaseMutableOverviewDialogProps<EntityT> = BaseMutableOverviewDialogProps<EntityT>,
> = BaseOverviewProps<EntityT, FilterT, SortingT, HeaderT, ActionButtonPropsT> & {
    mutationDialogProps?: Omit<HammockFormDialogProps<DialogBodyEntityT, DialogBodyPropsT>, 'dialogBody' | 'dialogFormBody'>
    mutationDialogFormProps?: (item: EntityT) => FormikConfig<DialogBodyEntityT>
    mutationDialogBody: FunctionComponent<HammockFormDialogProps<DialogBodyEntityT, DialogBodyPropsT>>
    mutationDialogBodyProps?: (item: EntityT) => DialogBodyPropsT
}

export type BaseMutableOverviewDialogProps<EntityT extends EntityDto> = {
    id?: string
    item?: EntityT
}

export type BaseMutableOverviewActionButtonProps<EntityT> = {
    openMutationDialog?: boolean
    setOpenMutationDialog?: (open: boolean, item?: EntityT) => void
}

/**
 * This Base Overview adds a HammockFormDialog to the BaseOverview component to (for example) allow items in the list to be edited or items to be added to the list.
 *
 * @param props see {@link BaseMutableOverviewProps} for more information on which fields to include.
 */
export default function BaseMutableOverview<
    EntityT extends EntityDto,
    FilterT extends BaseOverviewFilterType,
    SortingT extends BaseOverviewSortingType<any>,
    HeaderT extends BaseOverviewCount = BaseOverviewCount,
    ActionButtonProps = BaseMutableOverviewActionButtonProps<EntityT>,
    DefaultFilterTagsPropsT = any,
    DialogBodyEntityT = object,
    DialogBodyPropsT = object,
>(props: BaseMutableOverviewProps<EntityT, FilterT, SortingT, HeaderT, ActionButtonProps, DialogBodyEntityT, DialogBodyPropsT>) {
    const [openMutationDialog, setOpenMutationDialog] = useState(false)
    const [clickedItem, setClickedItem] = useState<EntityT>()

    const [reloadTrigger, setReloadTrigger] = useState<number>(0)
    const reload = () => {
        setReloadTrigger(reloadTrigger + 1)
    }

    return (
        <BaseOverview<EntityT, FilterT, SortingT, HeaderT, ActionButtonProps, DefaultFilterTagsPropsT>
            onItemClicked={props => {
                setClickedItem(props.item)
                setOpenMutationDialog(true)
            }}
            {...props}
            headerActionsButtonsProps={{
                ...props.headerActionsButtonsProps,
                openMutationDialog,
                setOpenMutationDialog: (open: boolean, item?: EntityT) => {
                    if (item !== undefined) {
                        setClickedItem(item)
                    }
                    setOpenMutationDialog(open)
                },
            }}
            reloadTrigger={reloadTrigger}
        >
            <HammockFormDialog<Partial<DialogBodyEntityT>, DialogBodyPropsT>
                open={openMutationDialog}
                setOpen={setOpenMutationDialog}
                {...props.mutationDialogProps}
                dialogFormProps={() => props.mutationDialogFormProps(clickedItem)}
                dialogFormBody={props.mutationDialogBody}
                dialogFormBodyProps={() => ({ id: clickedItem?.id, ...props.mutationDialogBodyProps?.(clickedItem) })}
                onDismiss={() => {
                    setOpenMutationDialog(false)
                    setClickedItem(undefined)
                    reload()
                }}
            />
        </BaseOverview>
    )
}
